import { SEO } from '../../utils/seo';

export default function TechnologyPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'テクノロジーと強み - Technology',
    description: 'Blockchain × AI × Real Service。ブロックチェーンによるデータの信頼性担保、AIエージェントによる業務自動化、実サービスとの組み合わせで、テクノロジーを現実の価値に変換します。',
    url: `${siteUrl}/technology`,
    mainEntity: {
      '@type': 'Article',
      headline: 'Blockchain × AI × Real Service',
      description: '私たちの事業は、すべて「テクノロジーを暮らしの現場で機能させること」を軸に設計されています。',
      author: {
        '@type': 'Organization',
        name: '株式会社ガイアライブラリー'
      }
    }
  };

  const technologies = [
    {
      icon: 'ri-links-line',
      title: 'Blockchain',
      description: 'ステーブルコイン、医療・個人情報の管理など、「改ざんされては困る」データの信頼性を担保。',
      color: 'blue',
      image: 'https://public.readdy.ai/ai/img_res/91c96f8f9fcd24bf50c5498988593acf.jpg'
    },
    {
      icon: 'ri-robot-line',
      title: 'AI / AIエージェント',
      description: '対話型AIによる健康相談、服薬支援、自治体業務の自動化など、人手不足を補いながらサービスレベルを向上。',
      color: 'teal',
      image: 'https://readdy.ai/api/search-image?query=Friendly%20AI%20assistant%20chatbot%20interface%20helping%20elderly%20person%2C%20conversational%20artificial%20intelligence%20for%20healthcare%20support%2C%20warm%20human-AI%20interaction%20scene%2C%20modern%20Japanese%20setting%20with%20blue%20accent%20colors%20and%20natural%20lighting&width=600&height=400&seq=tech-002&orientation=landscape'
    },
    {
      icon: 'ri-service-line',
      title: 'Real Service & Infrastructure',
      description: 'コーヒー輸入や防水コンクリートなど、実際の「モノ」「インフラ」との組み合わせで、テクノロジーを現実の価値に変換。',
      color: 'green',
      image: 'https://readdy.ai/api/search-image?query=Real%20world%20infrastructure%20services%20combining%20physical%20products%20and%20digital%20technology%2C%20coffee%20import%20logistics%20with%20smart%20tracking%2C%20construction%20materials%20with%20IoT%20sensors%2C%20practical%20technology%20implementation%20with%20blue%20and%20white%20color%20scheme&width=600&height=400&seq=tech-003&orientation=landscape'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="テクノロジーと強み｜福岡ブロックチェーンAI技術"
        description="Blockchain × AI × Real Service。ブロックチェーンによるデータの信頼性担保、AIエージェントによる業務自動化、実サービスとの組み合わせで、テクノロジーを現実の価値に変換します。"
        keywords="ブロックチェーン,AI,AIエージェント,技術,イノベーション,DX"
        schema={schema}
      />
      {/* Page Header */}
      <div className="relative bg-gradient-to-br from-blue-900 to-blue-700 text-white py-32 px-6 md:px-20 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://readdy.ai/api/search-image?query=Advanced%20technology%20convergence%20illustration%2C%20blockchain%20AI%20and%20real%20world%20services%20integration%2C%20futuristic%20digital%20infrastructure%20network%2C%20abstract%20tech%20visualization%20with%20blue%20glowing%20circuits%20and%20data%20streams%20on%20dark%20background&width=1920&height=600&seq=tech-header-001&orientation=landscape"
            alt="Technology Background"
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <p className="text-sm tracking-widest uppercase mb-4 text-blue-200">Technology</p>
          <h1 className="text-4xl md:text-5xl font-bold mb-6">テクノロジーと強み</h1>
          <p className="text-lg text-blue-100 leading-relaxed">
            Blockchain × AI × Real Service
          </p>
        </div>
      </div>

      {/* Main Content */}
      <section className="py-20 md:py-24 px-6 md:px-20">
        <div className="max-w-6xl mx-auto">
          {/* Introduction */}
          <div className="text-center mb-20">
            <p className="text-lg text-gray-700 leading-relaxed max-w-3xl mx-auto">
              私たちの事業は、すべて「テクノロジーを暮らしの現場で機能させること」を軸に設計されています。
            </p>
          </div>

          {/* Technology Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 mb-20">
            {technologies.map((tech, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 group"
              >
                {/* Image */}
                <div className="relative w-full h-56 overflow-hidden">
                  <img
                    src={tech.image}
                    alt={tech.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-blue-900/60 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 w-14 h-14 flex items-center justify-center bg-white rounded-xl shadow-lg">
                    <i className={`${tech.icon} text-3xl text-${tech.color}-600`}></i>
                  </div>
                </div>

                {/* Content */}
                <div className="p-8">
                  <h3 className="text-xl font-bold text-blue-900 mb-4">
                    {tech.title}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {tech.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
